'use client';
import React, { useCallback, useState, useMemo } from 'react';

import { Box } from '@nerdwallet/currency/components/Box';
import { RadioGroup } from '@nerdwallet/currency/components/RadioGroup';
import { SecureEncryptionMessage } from '@nerdwallet/currency/components/SecureEncryptionMessage';
import FloatyInput from '@nerdwallet/react-floaty-input';

import { useMutation } from '@apollo/client';
import { SegmentPageView } from '@lib/segment';
import { getLogger } from '@lib/logging';
import ContinueButton from '../components/continue-button';

import styles from './otp-confirmation.module.scss';
import FriendlyTitle from '../components/friendly-title';
import { useAnalytics } from '@lib/segment';
import { useTuVisibilityContext } from '@components/transunion-flow/tu-visibility-context';
import {
  TuStep,
  useTuStepper,
} from '@components/transunion-flow/tu-modal-stepper';
import { AUTHENTICATE_CREDIT_PROFILE_QUESTIONS_MUTATION } from '../queries';
import { hasOtpPasscodeQuestions } from '@components/transunion-flow/utils';
import { CreditProfileAuthStatusProfile } from '@generated-gql/generated-types';

const logger = getLogger('OTP Confirmation Step');

const formatNumber = (mobileNumber) =>
  mobileNumber &&
  `+1 ${mobileNumber.slice(1, 4)}-${mobileNumber.slice(4, 7)}-${mobileNumber.slice(7, 11)}`;

const phoneWarning =
  "Please make sure that the provided phone number is capable of receiving text messages. If you’d prefer to receive a code via a landline, select the 'phone call' option.";

const OtpConfirmationStep = () => {
  const { segmentTrack } = useAnalytics();
  const { setTuVisibility } = useTuVisibilityContext();
  const { tuCustomerInfo, authStatusProfile, setAuthStatusProfile } =
    useTuStepper();

  const handleSetTuAuthenticationSuccess = useCallback(
    (data: CreditProfileAuthStatusProfile) => {
      if (hasOtpPasscodeQuestions(data)) {
        setAuthStatusProfile(data);
        // Move on to inputting the passcode
        setTuVisibility({
          show: true,
          tuStep: TuStep.TU_OTP_PASSCODE_ENTRY,
        });
        return;
      }
    },
    [setAuthStatusProfile, setTuVisibility],
  );

  const [setTUAuthentication, { loading: isLoading }] = useMutation(
    AUTHENTICATE_CREDIT_PROFILE_QUESTIONS_MUTATION,
    {
      onCompleted: (data) => {
        handleSetTuAuthenticationSuccess(
          data.authenticateCreditProfileQuestions,
        );
      },
      onError: (err) => {
        // Only reason to error out on this step is if there's a coding error or TU is down
        logger.warn('Failed to submit OTP confirmation response', err);
      },
    },
  );
  const [otpConfirmationForm, setOtpConfirmationForm] = useState({
    mobilePhone: formatNumber(tuCustomerInfo.phoneNumber) || '',
    answer: undefined,
    method: '',
    validation: {
      mobilePhone: {
        isValid: !!tuCustomerInfo.phoneNumber,
      },
    },
  });

  const options = useMemo(() => {
    const questions = authStatusProfile?.questions ?? [];
    if (questions.length > 0) {
      return {
        questionId: questions[0].id,
        modifiedAnswers: questions[0].answers.map((answer) => {
          const isText = answer.text.toLowerCase().includes('text');
          return {
            value: answer.id,
            label: isText ? 'Text message' : 'Phone call',
          };
        }),
      };
    }
    return {
      questionId: '',
      modifiedAnswers: [],
    };
  }, [authStatusProfile]);

  const isContinueValid = useMemo(
    () =>
      otpConfirmationForm.validation.mobilePhone.isValid &&
      otpConfirmationForm.answer?.answerId,
    [otpConfirmationForm],
  );

  const sendCode = useCallback(
    (e) => {
      e.preventDefault();

      segmentTrack('TU OTP Send Code CTA Clicked', {
        otp_receive_method: otpConfirmationForm.method,
      });

      setTUAuthentication({
        variables: {
          answers: [otpConfirmationForm.answer],
        },
      });
    },
    [otpConfirmationForm, segmentTrack, setTUAuthentication],
  );

  const handleRadioChange = useCallback(
    (e) => {
      const finalValue = e.target.value;

      const method = options.modifiedAnswers.find(
        (answer) => answer.value === finalValue,
      )?.label;

      setOtpConfirmationForm((prevState) => ({
        ...prevState,
        method: method?.toLowerCase().includes('text')
          ? 'text_message'
          : 'call',
        answer: {
          questionId: options.questionId,
          answerId: finalValue,
        },
      }));
    },
    [options],
  );

  return (
    <SegmentPageView name="tu_otp_send_sms_verification">
      <Box display="flex" flexDirection="column">
        <FriendlyTitle
          title="How should we send your confirmation code?"
          subtext="We’ll send a code to the number shown below to verify your identity."
        />
        <form onSubmit={sendCode}>
          <div className={styles.inputContainer}>
            <div className={styles.phoneInput}>
              <FloatyInput
                label="Mobile Number"
                value={otpConfirmationForm.mobilePhone}
                placeholder="Mobile Number"
                type="tel"
                disabled
              />
            </div>
          </div>

          <p className={styles.phoneWarn} data-testid="phone-warning">
            {phoneWarning}
          </p>

          <RadioGroup
            name="otp-answer"
            onChange={handleRadioChange}
            options={options.modifiedAnswers}
          />

          <Box marginTop={4}>
            <ContinueButton
              isDisabled={!isContinueValid || isLoading}
              isLoading={isLoading}
              buttonText="Send code"
            />
          </Box>
          <Box marginTop={4}>
            <SecureEncryptionMessage />
          </Box>
        </form>
      </Box>
    </SegmentPageView>
  );
};

export default OtpConfirmationStep;
